import type { Channel } from "@sobidogam/platform-ports";
import { type ReactNode, useState } from "react";
import { lastErrorCode } from "./recent-error";
import { Sheet } from "./Sheet";

export const SUPPORT_EMAIL = "support@munops.com";
export const APP_VERSION = "0.4.0";

/**
 * #49 문제 알리기. The mail body starts with app version, channel, screen and
 * the last error code — shown and editable before sending. Record content
 * (매장·금액·메모) is never attached.
 */
export function issueReportBody(input: {
	channel: Channel;
	screen: string;
	code: string | null;
}): string {
	return [
		`앱 버전: ${APP_VERSION}`,
		`채널: ${input.channel === "ait" ? "토스 미니앱" : "웹"}`,
		`화면: ${input.screen}`,
		...(input.code ? [`오류 코드: ${input.code}`] : []),
		"",
		"어떤 일이 있었는지 적어 주세요. 매장·금액·복구코드는 적지 않아도 돼요.",
		"",
	].join("\n");
}

export function IssueReportButton({
	channel,
	screen,
	label = "문제 알리기",
	className = "quiet-button",
	onOpen,
}: {
	channel: Channel;
	screen: string;
	label?: ReactNode;
	className?: string;
	onOpen?: () => void;
}) {
	const [open, setOpen] = useState(false);
	return (
		<>
			<button
				className={className}
				type="button"
				onClick={() => {
					onOpen?.();
					setOpen(true);
				}}
			>
				{label}
			</button>
			{open ? (
				<IssueReportSheet
					channel={channel}
					screen={screen}
					onClose={() => setOpen(false)}
				/>
			) : null}
		</>
	);
}

function IssueReportSheet({
	channel,
	screen,
	onClose,
}: {
	channel: Channel;
	screen: string;
	onClose: () => void;
}) {
	const [body, setBody] = useState(() =>
		issueReportBody({ channel, screen, code: lastErrorCode() }),
	);
	const [notice, setNotice] = useState("");
	const subject = "소비도감 문제 알림";
	const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
	return (
		<Sheet title="문제 알리기" onClose={onClose}>
			<p className="field-hint">
				아래 내용이 그대로 메일에 들어가요. 지우거나 고쳐도 돼요.
			</p>
			<label className="issue-body">
				<span className="sr-only">보낼 내용</span>
				<textarea
					rows={8}
					value={body}
					onChange={(event) => setBody(event.target.value)}
				/>
			</label>
			<div className="issue-actions">
				{channel === "ait" ? null : (
					<a className="button full-width" href={href}>
						메일 앱으로 보내기
					</a>
				)}
				<button
					className={channel === "ait" ? "button full-width" : "quiet-button"}
					type="button"
					onClick={async () => {
						try {
							await navigator.clipboard.writeText(
								`받는 곳: ${SUPPORT_EMAIL}\n\n${body}`,
							);
							setNotice(`복사했어요. ${SUPPORT_EMAIL}로 보내 주세요.`);
						} catch {
							setNotice(
								`내용을 길게 눌러 복사한 뒤 ${SUPPORT_EMAIL}로 보내 주세요.`,
							);
						}
					}}
				>
					내용 복사
				</button>
			</div>
			<p className="field-hint">
				받는 곳: <b>{SUPPORT_EMAIL}</b> · 평일 3일 안에 답장을 목표로 해요.
			</p>
			{notice ? <output className="form-note">{notice}</output> : null}
		</Sheet>
	);
}
